import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Paintbrush2, Sparkles, FlaskConical, LayoutGrid } from "lucide-react";
import { api } from "../../store/slices/authSlice";
import { useTranslation } from "react-i18next";
import styles from "./CategoriesPage.module.css";

const CAT_CONFIG = {
  paints: {
    icon: Paintbrush2,
    gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    light: "rgba(245,158,11,0.08)",
    accent: "#d97706",
    key: "categories.paints" 
  },
  cosmetics: {
    icon: Sparkles,
    gradient: "linear-gradient(135deg, #ec4899 0%, #be185d 100%)",
    light: "rgba(236,72,153,0.08)",
    accent: "#be185d",
    key: "categories.cosmetics"
  },
  detergents: {
    icon: FlaskConical,
    gradient: "linear-gradient(135deg, #0c6839 0%, #064e2b 100%)",
    light: "rgba(12,104,57,0.08)",
    accent: "#0c6839",
    key: "categories.detergents"
  },
  others: {
    icon: LayoutGrid,
    gradient: "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)",
    light: "rgba(99,102,241,0.08)",
    accent: "#4338ca",
    key: "categories.others"
  },
};

const DEFAULT_CONFIG = {
  icon: LayoutGrid,
  gradient: "linear-gradient(135deg, #6b7280 0%, #374151 100%)",
  light: "rgba(107,114,128,0.08)",
  accent: "#374151",
};

export default function CategoriesPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get("/categories");
        if (res.data.code === 200) setCategories(res.data.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  return (
    <div className={styles.page} dir={isRtl ? "rtl" : "ltr"}>
      <div className={styles.container}>
        <div className={styles.header}>
          <span className={styles.badge}>{t("categories.badge")}</span>
          <h1 className={styles.title}>{t("categories.title")}</h1>
          <p className={styles.subtitle}>{t("categories.subtitle")}</p>
        </div>

        {loading ? (
          <div className={styles.grid}>
            {[...Array(4)].map((_, i) => (
              <div key={i} className={styles.skeleton} />
            ))}
          </div>
        ) : (
          <div className={styles.grid}>
            {categories.map((cat) => {
              const cfg = CAT_CONFIG[cat.name.toLowerCase()] || DEFAULT_CONFIG;
              const Icon = cfg.icon;
              const displayName = cfg.key ? t(cfg.key) : cat.name;

              return (
                <button
                  key={cat.id}
                  className={styles.card}
                  style={{
                    "--cat-light": cfg.light,
                    "--cat-accent": cfg.accent,
                    textAlign: isRtl ? "right" : "left"
                  }}
                  onClick={() =>
                    navigate(`/categories/${cat.id}`, {
                      state: { name: displayName },
                    })
                  }
                >
                  <div
                    className={styles.iconWrap}
                    style={{ background: cfg.gradient }}
                  >
                    <Icon size={32} color="#fff" strokeWidth={1.8} />
                  </div>
                  <h2 className={styles.catName}>{displayName}</h2>
                  <p className={styles.catHint}>
                    {t("categories.exploreHint")} {isRtl ? "←" : "→"}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}